import clsx from 'clsx';
import React, {Children, FC, ReactNode, useState} from 'react';
import {Typography} from 'onboarding-components';

import style from './content-tabs.module.styl';
/**
 *
 * @example
 * ```jsx
 * <TabContainer defaultActive={0} onTabChange={(index) => console.log(`Tab changed to ${index}`)}>
 *   <TabPanel heading="First Tab">
 *     Content for first tab
 *   </TabPanel>
 *   <TabPanel heading="Second Tab" disabled={true}>
 *     Content for second tab (disabled)
 *   </TabPanel>
 * </TabContainer>
 * ```
 */
interface TabProps {
  heading: string;
  children: ReactNode;
  id?: string;
  disabled?: boolean;
}

interface TabContainerProps {
  children: ReactNode;
  defaultActive?: number;
  onTabChange?: (index: number) => void;
  className?: string;
}

export const TabPanel: FC<TabProps> = () => {
  return null;
};

export const TabContainer: FC<TabContainerProps> = ({children, defaultActive = 0, onTabChange, className = ``}) => {
  const [activeTab, setActiveTab] = useState<number>(defaultActive);

  const tabData = Children.toArray(children).map((child: any, index) => ({
    heading: child.props?.heading || `Tab ${index + 1}`,
    id: child.props?.id || `tab-${index}`,
    disabled: child.props?.disabled || false,
    content: child.props?.children,
    index,
  }));

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    if (onTabChange) {
      onTabChange(index);
    }
  };

  return (
    <div className={className}>
      <div className={clsx(style.tabsHeader)} role='tablist'>
        {tabData.map((tab) => {
          const isActive = activeTab === tab.index;
          const isDisabled = tab.disabled;

          return (
            <button
              key={tab.id}
              className={clsx(style.tabButton, {
                [style.isActive]: isActive,
                [style.isDisabled]: isDisabled,
              })}
              onClick={isDisabled ? undefined : () => handleTabChange(tab.index)}
              aria-selected={isActive}
              aria-disabled={isDisabled}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              role='tab'
              tabIndex={isActive ? 0 : -1}
            >
              <Typography variant='captionXs' weight='bold'>
                {tab.heading}
              </Typography>
            </button>
          );
        })}
      </div>

      {tabData.map((tab) => {
        const isActive = activeTab === tab.index;

        if (!isActive) return null;

        return (
          <div
            key={tab.id}
            id={`tabpanel-${tab.id}`}
            className={clsx(style.tabPanel, style.isActive)}
            role='tabpanel'
            aria-labelledby={`tab-${tab.id}`}
            tabIndex={0}
          >
            {tab.content}
          </div>
        );
      })}
    </div>
  );
};
